import { useEffect, useMemo, useState } from 'react';
import { useApp } from '../lib/data/store';
import { supabase } from '../lib/supabaseClient';

/** Coordinator message pushed to the field — shown across screens, dismissible. */
export function TalkingPointsBanner() {
  const { myDistrict } = useApp();
  const [dismissed, setDismissed] = useState('');
  const tp = myDistrict?.talking_points?.trim();
  if (!tp || dismissed === tp) return null;
  return (
    <div className="banner-tp">
      <span>📣 {tp}</span>
      <button aria-label="Dismiss" onClick={() => setDismissed(tp)}>
        ✕
      </button>
    </div>
  );
}

/** Coordinator editor: set the talking-points message for a district. */
export function TalkingPointsEditor() {
  const { districts, profile, refresh } = useApp();
  // A coordinator may only message their OWN team's districts; an admin, any.
  const editable = useMemo(
    () =>
      profile && profile.role !== 'admin' && profile.candidateId
        ? districts.filter((x) => x.assigned_team === profile.candidateId)
        : districts,
    [districts, profile],
  );
  const [sel, setSel] = useState('');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const d = editable.find((x) => x.id === sel);

  // Keep the selection valid as the editable set loads/changes.
  useEffect(() => {
    if (!editable.some((x) => x.id === sel)) setSel(editable[0]?.id ?? '');
  }, [editable, sel]);

  useEffect(() => {
    setText(d?.talking_points ?? '');
    setSaved(null);
  }, [sel, d?.talking_points]);

  if (editable.length === 0) return null;

  return (
    <div className="card">
      <h2>Talking points</h2>
      <p className="muted" style={{ fontSize: 'calc(12px * var(--scale))' }}>
        A short message pushed to volunteers in this district (e.g. today’s priority issue).
      </p>
      <label className="field">
        <span>District</span>
        <select value={sel} onChange={(e) => setSel(e.target.value)}>
          {editable.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </select>
      </label>
      <textarea value={text} placeholder="e.g. Lead with property tax today; sign blitz on Quebec St." onChange={(e) => setText(e.target.value)} />
      {err && <p className="error">{err}</p>}
      {saved && (
        <p className="muted" style={{ color: 'var(--ok)' }}>
          ✓ Pushed to {saved}
        </p>
      )}
      <button
        className="btn btn-block"
        style={{ marginTop: 8 }}
        disabled={saving || !sel}
        onClick={async () => {
          setSaving(true);
          setErr(null);
          setSaved(null);
          const { error } = await supabase.rpc('set_talking_points', { p_district_id: sel, p_text: text });
          setSaving(false);
          if (error) setErr(error.message);
          else {
            setSaved(d?.name ?? 'district');
            await refresh();
          }
        }}
      >
        {saving ? 'Saving…' : 'Push to field'}
      </button>
    </div>
  );
}
